import { create } from "zustand";
import type { NamespaceSelectionCapability, RangeKey } from "@k8s-dashboard/contracts";
import {
  canonicalNamespaceSelection,
  namespaceSelectionBinding,
  namespaceSelectionLabel,
  type NamespaceSelection,
} from "./namespaceSelection";

/**
 * 전역 상태 스토어 (zustand)
 * --------------------------------------------------------------------------
 * 값마다 진실 원천이 다릅니다.
 *
 * - **range · refresh · path parameter** — URL이 원천입니다. 이 스토어는 읽기 사본이고,
 *   값이 어긋나면 항상 URL 값으로 덮어씁니다(`syncFromUrl`). 변경은
 *   `useDashboardParams().patch` → URL 갱신 → sync 순서로만 흐릅니다.
 * - **clusterId** — Observer는 단일 클러스터 배포라 선택하지 않습니다. AppShell이
 *   `/api/v1/scope`의 첫 접근 가능 클러스터로 채웁니다. URL에 두지 않습니다. (#94)
 * - **namespaceSelection** — Header의 커밋된 전역 선택입니다. URL에 두지 않고,
 *   AppShell이 검증된 partition·cluster·Scope fingerprint 전용 record로만 복원합니다.
 *   `namespace`는 기존 화면 라벨 호환용 파생값일 뿐 요청 권한이 아닙니다.
 *
 * 여기 값은 모두 요청 힌트입니다 — 실제 Scope는 서버가 토큰에서 강제합니다. (README §10)
 */

export interface DashboardParamsState {
  clusterId: string;
  namespace: string;
  namespaceSelection?: NamespaceSelection;
  namespaceSelectionStatus: "pending" | "ready" | "repair" | "ack-lost";
  namespaceSelectionCapability?: NamespaceSelectionCapability;
  namespaceSelectionGeneration: number;
  range: RangeKey;
  refreshMs: number;
  /** 현재 라우트의 path parameter (예: /pods/{name}의 name). */
  pathParams: Record<string, string | undefined>;
  /** URL에서 읽은 값으로 스토어를 맞춥니다. 값이 같으면 아무것도 하지 않습니다. */
  syncFromUrl(next: Pick<DashboardParamsState, "range" | "refreshMs" | "pathParams">): void;
  setClusterId(clusterId: string): void;
  /** Header의 전역 namespace 선택. `all`은 "고르지 않음"입니다. */
  setNamespace(namespace: string): void;
  setNamespaceSelectionContext(
    selection: NamespaceSelection | undefined,
    status: DashboardParamsState["namespaceSelectionStatus"],
    capability?: NamespaceSelectionCapability,
  ): void;
  commitNamespaceSelection(selection: NamespaceSelection, capability?: NamespaceSelectionCapability): void;
  markNamespaceSelectionAckLost(generation: number): void;
  retryNamespaceSelection(): void;
}

function sameRecord(a: Record<string, string | undefined>, b: Record<string, string | undefined>) {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
}

export const useDashboardStore = create<DashboardParamsState>((set, get) => ({
  clusterId: "",
  namespace: "all",
  namespaceSelection: undefined,
  namespaceSelectionStatus: "pending",
  namespaceSelectionCapability: undefined,
  namespaceSelectionGeneration: 0,
  range: "1h",
  refreshMs: 30_000,
  pathParams: {},
  syncFromUrl(next) {
    const cur = get();
    if (cur.range === next.range && cur.refreshMs === next.refreshMs && sameRecord(cur.pathParams, next.pathParams)) {
      return;
    }
    set(next);
  },
  setClusterId(clusterId) {
    if (get().clusterId !== clusterId) set({ clusterId });
  },
  setNamespace(namespace) {
    const next = namespace || "all";
    const selection = canonicalNamespaceSelection(next === "all"
      ? { mode: "all" }
      : { mode: "selected", names: [next] });
    if (selection) get().commitNamespaceSelection(selection, get().namespaceSelectionCapability);
  },
  setNamespaceSelectionContext(selection, status, capability) {
    const canonical = selection ? canonicalNamespaceSelection(selection) : undefined;
    const cur = get();
    const label = canonical ? namespaceSelectionLabel(canonical) : "all";
    if ((cur.namespaceSelection ? namespaceSelectionBinding(cur.namespaceSelection) : "") ===
        (canonical ? namespaceSelectionBinding(canonical) : "") && cur.namespaceSelectionStatus === status &&
        cur.namespaceSelectionCapability === capability) return;
    set({
      namespace: label,
      namespaceSelection: canonical,
      namespaceSelectionStatus: canonical ? status : status === "ready" ? "repair" : status,
      namespaceSelectionCapability: capability,
      namespaceSelectionGeneration: cur.namespaceSelectionGeneration + 1,
    });
  },
  commitNamespaceSelection(selection, capability) {
    const canonical = canonicalNamespaceSelection(selection);
    if (!canonical) return;
    const cur = get();
    set({
      namespace: namespaceSelectionLabel(canonical),
      namespaceSelection: canonical,
      namespaceSelectionStatus: "ready",
      namespaceSelectionCapability: capability,
      namespaceSelectionGeneration: cur.namespaceSelectionGeneration + 1,
    });
  },
  markNamespaceSelectionAckLost(generation) {
    const cur = get();
    if (cur.namespaceSelectionGeneration !== generation || cur.namespaceSelectionStatus !== "ready") return;
    set({ namespaceSelectionStatus: "ack-lost", namespaceSelectionGeneration: generation + 1 });
  },
  retryNamespaceSelection() {
    const cur = get();
    if (!cur.namespaceSelection || cur.namespaceSelectionStatus !== "ack-lost") return;
    set({ namespaceSelectionStatus: "ready", namespaceSelectionGeneration: cur.namespaceSelectionGeneration + 1 });
  },
}));
